import type { COMPANY_ACTIVE_STATUS } from '@/features/Account/types/constants';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { defaultAxios } from '../../../services';

const companyService = {
  async fetchCompanyList(query?: string): Promise<PaginatedData<CompanyT>[]> {
    let retry = 0;
    const request = async (): Promise<PaginatedData<CompanyT>[]> => {
      try {
        const wa = getCurrWorkspaceAlias();
        const res = await defaultAxios.get(
          `workspaces/${wa}/companies${query || ''}`,
        );
        return res.data.companies;
      } catch (e) {
        if (retry < 10) {
          retry += 1;
          return await request();
        } else {
          throw e;
        }
      }
    };
    return await request();
  },
  async fetchCompanyCount(query?: string): Promise<number> {
    try {
      const wa = getCurrWorkspaceAlias();
      const res = await defaultAxios.get(
        `workspaces/${wa}/companies/count${query || ''}`,
      );
      return res.data.count;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  async fetchCompany(companyAlias: string): Promise<CompanyT> {
    try {
      const res = await defaultAxios.get(`/companies/${companyAlias}`);
      return res.data.company;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  postCompany: async (company: PostCompanyParams): Promise<CompanyT> => {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(`workspaces/${wa}/company`, company);
    return res.data;
  },
  uploadCompanies: async (companies: csvCompanyParams[]) => {
    const wa = getCurrWorkspaceAlias();
    const res = await defaultAxios.post(
      `workspaces/${wa}/company/upload`,
      companies,
    );
    return res.data;
  },
  deleteCompany: async (alias: string) => {
    try {
      const res = await defaultAxios.delete(`companies/${alias}`);
      return res.data;
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  changeCompanyName: async (key: string, name: string) => {
    try {
      await defaultAxios.patch(`companies/${key}/name`, {
        name,
      });
      return true;
    } catch {
      return false;
    }
  },
  changeCompanyCount: async (key: string, count: number) => {
    try {
      await defaultAxios.patch(`companies/${key}/count`, {
        count,
      });
      return true;
    } catch {
      return false;
    }
  },
  changeCompanyFavorite: async (key: string, favorite: boolean) => {
    try {
      await defaultAxios.put(`companies/${key}/favorite`, {
        favorite,
      });
      return true;
    } catch {
      return false;
    }
  },
  changeSize: async (key: string, size: number) => {
    try {
      await defaultAxios.patch(`companies/${key}/size`, {
        size,
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  changeRegion: async (key: string, region: string) => {
    try {
      await defaultAxios.patch(`companies/${key}/region`, {
        region,
      });
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  changeStatus: async (key: string, status: COMPANY_ACTIVE_STATUS) => {
    try {
      await defaultAxios.patch(`companies/${key}/status`, { status });
    } catch (e) {
      console.error(e);
      throw e;
    }
  },
  updateCompanyAttr: async <K extends keyof CompanyT>(
    alias: string,
    target: K,
    value: CompanyT[K],
  ) => {
    try {
      if (typeof value === 'undefined') {
        const res = await defaultAxios.patch(
          `companies/${alias}/${target}/reset`,
        );
        return res.data;
      }
      await defaultAxios.patch(`companies/${alias}/${target}`, {
        [target]: value,
      });
      return true;
    } catch (e) {
      return false;
    }
  },
  resetCompanyAttr: async <K extends keyof CompanyT>(
    alias: string,
    target: K,
  ) => {
    try {
      await defaultAxios.patch(`companies/${alias}/${target}/reset`);
      return true;
    } catch (e) {
      return false;
    }
  },
  async fetchLinkedFeedback(
    companyAlias: string,
  ): Promise<LinkedFeedbackResponse> {
    const res = await defaultAxios.get(`/companies/${companyAlias}/feedbacks`);
    return res.data;
  },
  async fetchAccountName(accountAliases: string[]): Promise<AliasNamePair[]> {
    const res = await defaultAxios.post(`/companies/name`, {
      aliases: accountAliases,
    });
    return res.data;
  },
};

export default companyService;
